import { createSlice } from '@reduxjs/toolkit';
// utils
import { axiosInstance as axios } from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const SESSION_KEY_PREFIX = 'sowan_chat_session_id';
const SESSION_TIME_KEY_PREFIX = 'sowan_chat_last_message_at';
const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour

// Chat sessions are scoped per user + business so switching businesses, or a
// different user logging in on the same browser, can never surface someone
// else's conversation. Anonymous (logged-out) visitors share a single unscoped key.
const scopeSuffix = (userId, businessId) => (userId ? `_${userId}_${businessId || 'none'}` : '');
const sessionKey = (userId, businessId) => `${SESSION_KEY_PREFIX}${scopeSuffix(userId, businessId)}`;
const sessionTimeKey = (userId, businessId) => `${SESSION_TIME_KEY_PREFIX}${scopeSuffix(userId, businessId)}`;

const getStoredSessionId = (userId, businessId) => {
  const sessionId = localStorage.getItem(sessionKey(userId, businessId));
  const lastMessageAt = localStorage.getItem(sessionTimeKey(userId, businessId));
  if (sessionId && lastMessageAt) {
    const elapsed = Date.now() - parseInt(lastMessageAt, 10);
    if (elapsed < SESSION_TIMEOUT) {
      return sessionId;
    }
  }
  return null;
};

const generateSessionId = (userId, businessId) => {
  const id = `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem(sessionKey(userId, businessId), id);
  localStorage.setItem(sessionTimeKey(userId, businessId), Date.now().toString());
  return id;
};

// ----------------------------------------------------------------------

const initialState = {
  isOpen: false,
  isLoading: false,
  isSending: false,
  error: false,
  messages: [],
  sessionId: null,
  userId: null,
  businessId: null,
  unreadCount: 0,
  userContext: null,
  conversationStatus: 'active',
  autoReplyEnabled: true,
};

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // TOGGLE CHAT
    toggleChat(state) {
      state.isOpen = !state.isOpen;
      if (state.isOpen) {
        state.unreadCount = 0;
      }
    },

    openChat(state) {
      state.isOpen = true;
      state.unreadCount = 0;
    },

    closeChat(state) {
      state.isOpen = false;
    },

    // SET SESSION
    setSessionId(state, action) {
      state.sessionId = action.payload;
    },

    // SET USER CONTEXT
    setUserContext(state, action) {
      state.userContext = action.payload;
    },

    // SENDING
    startSending(state) {
      state.isSending = true;
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.isSending = false;
      state.error = action.payload;
    },

    // SEND MESSAGE SUCCESS
    sendMessageSuccess(state, action) {
      state.isSending = false;
      state.error = false;
      const { userMessage, aiResponse, sessionId, attachments, savedMessages } = action.payload;
      state.sessionId = sessionId;
      // Use the server-saved messages so their ids match what polling returns;
      // a poll that ran while the AI was replying may already have added them.
      if (savedMessages && savedMessages.length > 0) {
        const existingIds = new Set(state.messages.map((m) => m.id));
        state.messages.push(...savedMessages.filter((m) => !existingIds.has(m.id)));
        return;
      }
      // Add user message
      state.messages.push({
        id: `user-${Date.now()}`,
        role: 'user',
        sender_type: 'customer',
        content: userMessage,
        attachments: attachments || [],
        created_at: new Date().toISOString(),
      });
      // Add AI response if available
      if (aiResponse) {
        state.messages.push({
          id: `ai-${Date.now()}`,
          role: 'assistant',
          sender_type: 'ai',
          content: aiResponse,
          attachments: [],
          created_at: new Date().toISOString(),
        });
      }
    },

    // LOAD HISTORY SUCCESS
    loadHistorySuccess(state, action) {
      state.isLoading = false;
      state.error = false;
      state.messages = action.payload.messages || [];
    },

    // POLL MESSAGES SUCCESS
    pollMessagesSuccess(state, action) {
      const { messages, status: convStatus, auto_reply_enabled: autoReply } = action.payload;
      if (messages && messages.length > 0) {
        // Filter out messages we already have
        const existingIds = new Set(state.messages.map((m) => m.id));
        const newMessages = messages.filter((m) => !existingIds.has(m.id));
        if (newMessages.length > 0) {
          state.messages.push(...newMessages);
          // Count unread if chat is closed
          if (!state.isOpen) {
            const operatorMessages = newMessages.filter((m) => m.sender_type === 'operator' || m.sender_type === 'ai');
            state.unreadCount += operatorMessages.length;
          }
        }
      }
      if (convStatus) {
        state.conversationStatus = convStatus;
      }
      if (autoReply !== undefined) {
        state.autoReplyEnabled = autoReply;
      }
    },

    // SWITCH TO A (POSSIBLY DIFFERENT) USER/BUSINESS CONVERSATION
    // Wipes the in-memory conversation so the previous one's messages never
    // render for the new one; the previous history stays in localStorage
    // under its own key and comes back if that user/business is active again.
    startChatForOwner(state, action) {
      state.userId = action.payload.userId || null;
      state.businessId = action.payload.businessId || null;
      state.messages = [];
      state.sessionId = null;
      state.unreadCount = 0;
      state.conversationStatus = 'active';
      state.autoReplyEnabled = true;
      state.error = false;
    },

    // CLEAR CHAT (e.g. on logout, when no business is active anymore)
    clearActiveChat(state) {
      state.userId = null;
      state.businessId = null;
      state.messages = [];
      state.sessionId = null;
      state.unreadCount = 0;
      state.conversationStatus = 'active';
      state.autoReplyEnabled = true;
      state.error = false;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { toggleChat, openChat, closeChat, setUserContext } = slice.actions;

// ----------------------------------------------------------------------

// True while the chat is still showing the given session; a response that
// arrives after the user/business (and so the session) changed is stale.
const isCurrentSession = (getState, sessionId) => getState().chat.sessionId === sessionId;

export function initChat({ userId, businessId } = {}) {
  return async (_, getState) => {
    dispatch(slice.actions.startChatForOwner({ userId, businessId }));

    const existingSessionId = getStoredSessionId(userId, businessId);
    if (existingSessionId) {
      dispatch(slice.actions.setSessionId(existingSessionId));
      // Load existing history
      dispatch(slice.actions.startLoading());
      try {
        const response = await axios.get(`/support/conversation/${existingSessionId}`);
        if (!isCurrentSession(getState, existingSessionId)) return;
        dispatch(slice.actions.loadHistorySuccess(response.data));
      } catch (error) {
        if (!isCurrentSession(getState, existingSessionId)) return;
        // No history found (or not ours anymore) — start a fresh conversation
        dispatch(slice.actions.setSessionId(null));
        dispatch(slice.actions.hasError(false));
      }
    }
  };
}

// On logout: clear the widget. The user's stored session is kept so their
// conversation comes back if they log in again within the session timeout;
// it's keyed by user id and the backend only serves it to that user.
export function endChatSession() {
  return () => {
    dispatch(slice.actions.clearActiveChat());
  };
}

export function sendMessage(messageText, files = []) {
  return async (_, getState) => {
    dispatch(slice.actions.startSending());
    try {
      const { chat } = getState();
      const sessionId = chat.sessionId || generateSessionId(chat.userId, chat.businessId);

      let response;
      if (files && files.length > 0) {
        const formData = new FormData();
        formData.append('message', messageText || '');
        formData.append('session_id', sessionId);
        if (chat.userContext) {
          formData.append('visitor_name', chat.userContext.name || '');
          formData.append('visitor_email', chat.userContext.email || '');
        }
        files.forEach((f) => formData.append('files', f));
        response = await axios.post('/support/chat', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const payload = { message: messageText, session_id: sessionId };
        if (chat.userContext) {
          payload.visitor_name = chat.userContext.name;
          payload.visitor_email = chat.userContext.email;
        }
        response = await axios.post('/support/chat', payload);
      }

      // Update last message timestamp
      localStorage.setItem(sessionTimeKey(chat.userId, chat.businessId), Date.now().toString());
      localStorage.setItem(sessionKey(chat.userId, chat.businessId), sessionId);

      // The user/business changed (or logged out) while the message was in
      // flight; it was saved to its own conversation, so don't render it in the new one.
      const { chat: current } = getState();
      if (current.userId !== chat.userId || current.businessId !== chat.businessId) {
        dispatch(slice.actions.hasError(false));
        return;
      }

      dispatch(
        slice.actions.sendMessageSuccess({
          userMessage: messageText,
          aiResponse: response.data.response,
          sessionId,
          attachments: response.data.attachments || [],
          savedMessages: response.data.messages,
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function pollMessages(since) {
  return async (_, getState) => {
    try {
      const { chat } = getState();
      if (!chat.sessionId) return;

      const params = since ? { since } : {};
      const response = await axios.get(`/support/chat/poll/${chat.sessionId}/`, { params });
      if (!isCurrentSession(getState, chat.sessionId)) return;
      dispatch(slice.actions.pollMessagesSuccess(response.data));
    } catch (error) {
      // Silent fail for polling
      console.error('Poll error:', error);
    }
  };
}
